'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { ResponsiveModal } from '@west-tennessee-consulting/ui/components/responsive-modal';
import { useGlobalModalStack } from '@west-tennessee-consulting/ui/components/global-modal-stack';
import { getAuthToken } from '../lib/api/authenticated-fetch.js';

export const CHAT_TOKEN_USAGE_MODAL_ID = 'chat-token-usage';

export interface ChatUsageResponse {
  chatId: string;
  stepCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  totalCostUsd: number;
  contextWindow: {
    maxMessages: number;
    maxChars: number;
    estimatedMaxTokens: number;
    charsPerTokenEstimate: number;
  };
}

export interface ChatTokenUsageModalProps {
  chatId: string;
  apiBaseUrl?: string;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function formatUsd(n: number): string {
  if (n <= 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/**
 * Modal showing lifetime Bedrock token usage for the open chat, plus the soft
 * context-window budgets Meridian uses when trimming history.
 */
export function ChatTokenUsageModal({
  chatId,
  apiBaseUrl = '/api/meridian',
}: ChatTokenUsageModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const open = isModalOpen(CHAT_TOKEN_USAGE_MODAL_ID);
  const [usage, setUsage] = useState<ChatUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usageApiBase = apiBaseUrl.includes('entity-chat')
    ? '/api/meridian'
    : apiBaseUrl.replace(/\/$/, '');

  const load = useCallback(async () => {
    if (!chatId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${usageApiBase}/usage?chatId=${encodeURIComponent(chatId)}`,
        { headers, credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error(res.status === 404 ? 'Chat not found' : 'Failed to load usage');
      }
      const data = (await res.json()) as ChatUsageResponse;
      setUsage(data);
    } catch (e) {
      setUsage(null);
      setError(e instanceof Error ? e.message : 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [usageApiBase, chatId]);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  return (
    <ResponsiveModal
      globalModalId={CHAT_TOKEN_USAGE_MODAL_ID}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeModal(CHAT_TOKEN_USAGE_MODAL_ID);
      }}
      title="Chat token usage"
    >
      <div className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Totals for this chat from Bedrock (all model steps). The context window
          is what Meridian keeps in the live prompt — older turns are summarized
          and trimmed when needed.
        </p>

        {loading && <p className="text-muted-foreground">Loading…</p>}
        {error && <p className="text-destructive">{error}</p>}

        {usage && !loading && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total tokens" value={formatTokens(usage.totalTokens)} />
              <Stat label="Est. cost" value={formatUsd(usage.totalCostUsd)} />
              <Stat label="Input" value={formatTokens(usage.inputTokens)} />
              <Stat label="Output" value={formatTokens(usage.outputTokens)} />
              <Stat
                label="Cache write"
                value={formatTokens(usage.cacheCreationInputTokens)}
              />
              <Stat
                label="Cache read"
                value={formatTokens(usage.cacheReadInputTokens)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {usage.stepCount} model step{usage.stepCount === 1 ? '' : 's'} recorded
            </p>

            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-1">
              <p className="text-xs font-medium text-foreground">Context window (live prompt)</p>
              <p className="text-xs text-muted-foreground">
                Up to {usage.contextWindow.maxMessages} recent messages and ~
                {formatTokens(usage.contextWindow.estimatedMaxTokens)} tokens of
                history (~{usage.contextWindow.maxChars.toLocaleString()} chars).
                A rolling AI summary covers earlier turns when present.
              </p>
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
